import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { FileText, Stamp, Loader2, Pencil, RotateCcw } from "lucide-react";
import { hr, api } from "../lib/api";
import { toast } from "sonner";

/**
 * KLASA/URBROJ input dialog used by Secretariat to issue a Decision PDF.
 * Iter79: adds collapsible "Uredi tekst rješenja" section for VACATION requests
 * — user can override body text (točke I-IV) before PDF preview.
 */
export function GeneratePdfDialog({
  open, onClose,
  klasa, urbroj,
  onKlasaChange, onUrbrojChange,
  onGenerate, busy,
  // Iter79
  customBodyText, onCustomBodyChange,
  // Iter88: opcijski datum izrade rješenja (retroaktivno unošenje povijesnih odluka)
  decisionIssuedAt, onDecisionIssuedAtChange,
  // Iter89: rješenje "po povratku" — bez fiksnog perioda, fond se ne oduzima
  deferPeriod, onDeferPeriodChange,
}) {
  const [showEdit, setShowEdit] = useState(false);
  const [defaultBody, setDefaultBody] = useState("");
  const [loadingBody, setLoadingBody] = useState(false);

  // Iter79: dohvat default body-a kad se dialog otvori (samo za vacation)
  // Iter89b: koristi ?defer=1 query param — mijenja template kad je "po povratku"
  useEffect(() => {
    if (!open || open?.type !== "vacation") return;
    setLoadingBody(true);
    setShowEdit(false);
    api.get(`/vacation/requests/${open.item.id}/default-body`)
      .then(r => {
        setDefaultBody(r.data.default_body_text || "");
        // Ako je već spremljen custom, pre-populiraj Textarea
        if (r.data.current_custom_body_text) {
          onCustomBodyChange(r.data.current_custom_body_text);
          setShowEdit(true);
        }
        // Iter88: pre-populiraj polje datuma iz backend-a (ako je već spremljen)
        if (onDecisionIssuedAtChange) {
          onDecisionIssuedAtChange(r.data.current_decision_issued_at || "");
        }
        // Iter89: pre-populiraj checkbox iz backend-a
        if (onDeferPeriodChange) {
          onDeferPeriodChange(!!r.data.current_deferred_period);
        }
      })
      .catch(() => { setDefaultBody(""); })
      .finally(() => setLoadingBody(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Iter89b: kad user toggle-a "po povratku" checkbox, re-dohvati default template
  // (deferred vs. standard). Ne diramo custom_body_text jer je user možda editirao.
  useEffect(() => {
    if (!open || open?.type !== "vacation") return;
    api.get(`/vacation/requests/${open.item.id}/default-body?defer=${deferPeriod ? 1 : 0}`)
      .then(r => setDefaultBody(r.data.default_body_text || ""))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deferPeriod]);

  const useDefault = () => {
    onCustomBodyChange(defaultBody);
    toast.info("Predložak vraćen na default — možete uređivati");
  };

  const clearCustom = () => {
    onCustomBodyChange("");
    setShowEdit(false);
    toast.info("Custom tekst uklonjen — koristi se automatski generiran default");
  };

  return (
    <Dialog open={!!open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="serif text-2xl">Generiranje rješenja</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="text-sm bg-secondary p-3 rounded">
            <FileText className="w-4 h-4 inline mr-1" /> Zaposlenik:{" "}
            <span className="font-medium">{open?.item?.employee_name}</span><br/>
            {open?.type === "wfh"
              ? <>Razdoblje: {open && `${hr(open.item.start_date)} – ${hr(open.item.end_date)}`}{open?.item?.broj_mobitela ? <> · Mob.: {open.item.broj_mobitela}</> : (open?.item?.lokacija ? <> · Lokacija: {open.item.lokacija}</> : null)}</>
              : <>Period: {open && `${hr(open.item.collective_start)} – ${hr(open.item.collective_end)}`}</>}
          </div>
          <div className="space-y-2">
            <Label>KLASA</Label>
            <Input value={klasa} onChange={(e) => onKlasaChange(e.target.value)}
                   placeholder={open?.type === "vacation" ? "113-02/26-01/001 (godišnji odmor)" : "Auto-generirano ako ostavite prazno"}
                   data-testid="klasa-input" />
            {open?.type === "vacation" && (
              <p className="text-[11px] text-muted-foreground">Standardni format za sve zaposlenike. Promijenite samo ako je nužno.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>URBROJ</Label>
            <Input value={urbroj} onChange={(e) => onUrbrojChange(e.target.value)}
                   placeholder={open?.type === "vacation" ? "251-75-03-26-NNN (NNN = redni broj iz Excela)" : "Auto-generirano ako ostavite prazno"}
                   data-testid="urbroj-input" />
            {open?.type === "vacation" && (
              <p className="text-[11px] text-muted-foreground">Posljednje 3 znamenke = redni broj zaposlenika iz KIF Excel popisa.</p>
            )}
          </div>

          {/* Iter89: rješenje "po povratku" — bez fiksnog perioda, fond se ne oduzima */}
          {open?.type === "vacation" && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
              <label
                className="flex items-start gap-2 cursor-pointer select-none"
                data-testid="defer-period-label"
              >
                <input
                  type="checkbox"
                  checked={!!deferPeriod}
                  onChange={(e) => onDeferPeriodChange && onDeferPeriodChange(e.target.checked)}
                  className="mt-0.5 h-4 w-4"
                  data-testid="defer-period-checkbox"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-900">
                    Rješenje po povratku (bez datuma korištenja)
                  </div>
                  <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                    PDF se izrađuje bez konkretnih datuma; radnik ih dogovara i naknadno
                    unosi po povratku. <b>Fond dana se NE oduzima</b> dok se ne unese stvarni period.
                    Ovo je namijenjeno za slučajeve kad se rješenje mora izdati unaprijed,
                    a datum korištenja još nije poznat.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Iter88: datum izrade rješenja — opcijski override za povijesne odluke */}
          {open?.type === "vacation" && (
            <div className="space-y-2">
              <Label>Datum izrade rješenja</Label>
              <Input
                type="date"
                value={decisionIssuedAt || ""}
                onChange={(e) => onDecisionIssuedAtChange && onDecisionIssuedAtChange(e.target.value)}
                data-testid="decision-issued-at-input"
                className="w-full sm:w-56"
              />
              <p className="text-[11px] text-muted-foreground">
                Ostavite prazno za današnji datum. Ovo polje mijenja datum u zaglavlju rješenja
                (<i>Zagreb, DD.MM.YYYY.</i>) — koristi se za retroaktivno unošenje povijesnih odluka.
              </p>
            </div>
          )}

          {/* Iter79: Uredi tekst rješenja — samo za pojedinačna vacation rješenja.
             Iter89b: vidljivo i kad je "po povratku" — user može editirati generički
             tekst prije izrade (defer flag i dalje kontrolira oduzimanje fonda). */}
          {open?.type === "vacation" && (
            <div className="pt-2 border-t space-y-2">
              {!showEdit ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => { onCustomBodyChange(defaultBody); setShowEdit(true); }}
                  disabled={loadingBody}
                  data-testid="edit-body-btn"
                >
                  {loadingBody
                    ? <><Loader2 className="w-4 h-4 mr-2 animate-spin"/> Učitavanje predloška…</>
                    : <><Pencil className="w-4 h-4 mr-2"/> Uredi tekst rješenja</>
                  }
                </Button>
              ) : (
                <div className="space-y-2 rounded-md border border-amber-300 bg-amber-50/40 p-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-medium">Tekst rješenja (točke I - IV)</Label>
                    <div className="flex gap-1">
                      <Button type="button" variant="ghost" size="sm" onClick={useDefault} data-testid="reset-body-btn">
                        <RotateCcw className="w-3 h-3 mr-1"/> Vrati na default
                      </Button>
                      <Button type="button" variant="ghost" size="sm" onClick={clearCustom} data-testid="cancel-body-btn">
                        Odustani
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    rows={18}
                    className="font-mono text-xs leading-relaxed"
                    value={customBodyText || ""}
                    onChange={(e) => onCustomBodyChange(e.target.value)}
                    data-testid="custom-body-input"
                  />
                  <p className="text-[11px] text-muted-foreground space-y-1">
                    <span className="block">
                      • Redovi koji počinju s <b>I.</b>, <b>II.</b>, <b>III.</b>, <b>IV.</b> renderiraju se kao <b>bold</b> naslovi točki.
                    </span>
                    <span className="block">
                      • Prazan red = razmak između paragrafa. U tekstu možete koristiti <code className="bg-secondary px-1 rounded text-[10px]">&lt;b&gt;tekst&lt;/b&gt;</code> za bold.
                    </span>
                    <span className="block">
                      • Ako ostavite prazno i pritisnete Odustani → koristi se automatski generiran default (s trenutnim datumima i danima).
                    </span>
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Odustani</Button>
          <Button onClick={onGenerate} disabled={busy} data-testid="confirm-generate">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Stamp className="w-4 h-4 mr-2"/>}
            Generiraj i pregledaj
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
